import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './RemoteController.css';
import './RemoteCode.css';
import { Bunny, GetFluffer, Header } from './RemoteController';
import { setCodeVisit } from './codeVisit';

/** Fluffer's API (code redemption). Test builds point VITE_FLUFFER_API at the test admin. */
const API = ((import.meta.env.VITE_FLUFFER_API as string | undefined) || 'https://admin.fluffer.me').replace(/\/+$/, '');

/** Codes never use 0, O, 1 or I, so nobody has to guess which one they're looking at. */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const LENGTH = 5;

type Problem = 'bad' | 'used' | 'busy' | 'later' | 'network' | null;

const PROBLEM_TEXT: Record<Exclude<Problem, null>, string> = {
  bad: 'That code didn’t work. Check it, or ask for a new one (codes last 10 minutes and work once).',
  used: 'That code was already used. Ask for a new one.',
  busy: 'Too many tries. Wait a few minutes and try again.',
  later: 'Fluffer is busy right now. Try again in a moment.',
  network: 'Couldn’t reach Fluffer. Check your connection and tap Connect to try again.',
};

/** Typed or pasted text → code characters (uppercase, no spaces or dashes); `odd` if something isn't in a code. */
function clean(text: string) {
  const chars = text.toUpperCase().replace(/[\s-]/g, '').split('');
  const ok = chars.filter(c => ALPHABET.includes(c));
  return { chars: ok, odd: ok.length !== chars.length };
}

const emptyBoxes = () => new Array<string>(LENGTH).fill('');

const RemoteCode: React.FC = () => {
  const navigate = useNavigate();
  const [boxes, setBoxes] = useState<string[]>(emptyBoxes);
  const [hint, setHint] = useState(false);
  const [problem, setProblem] = useState<Problem>(null);
  const [busy, setBusy] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const code = boxes.join('');
  const complete = code.length === LENGTH && boxes.every(Boolean);

  const focusBox = (i: number) => {
    const el = inputs.current[Math.max(0, Math.min(LENGTH - 1, i))];
    el?.focus();
    el?.select();
  };

  const redeem = useCallback(
    async (value: string) => {
      if (value.length !== LENGTH) return;
      setBusy(true);
      setProblem(null);
      try {
        const res = await fetch(`${API}/api/v1/remote/codes/redeem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: value }),
        });
        if (res.status === 429) return setProblem('busy');
        // Someone already came in with it: only the sharer can make a new one.
        if (res.status === 410) return setProblem('used');
        // Busy on our side, not a wrong code: the code stays in the boxes.
        if (res.status === 503) return setProblem('later');
        if (!res.ok) return setProblem(res.status >= 500 ? 'network' : 'bad');
        const data = (await res.json()) as { roomId?: string; lobbyKey?: string; deviceType?: string };
        if (!data.roomId || !data.lobbyKey) return setProblem('network');
        // Only the lobby: the room's key arrives when the sharer lets them in.
        // Handed over in memory, never in the URL. Replace, so Back doesn't
        // land on a used code and send it again.
        setCodeVisit({ roomId: data.roomId, lobbyKey: data.lobbyKey, deviceType: data.deviceType });
        navigate('/', { replace: true, state: { via: 'code' } });
      } catch {
        setProblem('network');
      } finally {
        setBusy(false);
      }
    },
    [navigate],
  );

  /** Put characters in from box `at` onward (typing one, or pasting all five). */
  const fill = (at: number, text: string) => {
    const { chars, odd } = clean(text);
    setHint(odd);
    setProblem(null);
    if (!chars.length) return;
    const start = chars.length >= LENGTH ? 0 : at;
    const next = [...boxes];
    chars.slice(0, LENGTH - start).forEach((c, k) => (next[start + k] = c));
    setBoxes(next);
    focusBox(Math.min(LENGTH - 1, start + chars.length));
  };

  const onChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (!v) {
      setBoxes(b => b.map((c, k) => (k === i ? '' : c)));
      return;
    }
    // A box already holding a character gets the new one typed over it.
    const typed = v.length > 1 && boxes[i] && v.includes(boxes[i]) ? v.replace(boxes[i], '') : v;
    fill(i, typed);
  };

  const onKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !boxes[i] && i > 0) {
      e.preventDefault();
      setBoxes(b => b.map((c, k) => (k === i - 1 ? '' : c)));
      focusBox(i - 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(i + 1);
    } else if (e.key === 'Enter' && complete && !busy) {
      redeem(code);
    }
  };

  const onPaste = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    fill(i, e.clipboardData.getData('text'));
  };

  // A link with ?code=XXXXX only fills the boxes: the person taps Connect.
  // Never redeemed on load, so a link previewer or scanner that runs the
  // page can't spend the single-use code (and knock in their place).
  const fromUrl = useRef(false);
  useEffect(() => {
    if (fromUrl.current) return;
    fromUrl.current = true;
    const given = new URLSearchParams(window.location.search).get('code') || '';
    const { chars, odd } = clean(given);
    if (!given) {
      focusBox(0);
      return;
    }
    try {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    } catch {
      /* stays in the address bar */
    }
    const next = emptyBoxes();
    chars.slice(0, LENGTH).forEach((c, k) => (next[k] = c));
    setBoxes(next);
    setHint(odd);
    focusBox(Math.min(LENGTH - 1, chars.length));
  }, []);

  return (
    <div className="rc">
      <Header connected={false} hideStatus />
      <main className="content">
        <div className="stage code-stage">
          <form
            className="card code-card"
            onSubmit={e => {
              e.preventDefault();
              if (complete && !busy) redeem(code);
            }}
          >
            <Bunny size={84} />
            <h1>Enter the code</h1>
            <p className="code-help">Ask the person sharing for the 5-character code on their screen.</p>

            <div className="code-boxes" role="group" aria-label="5-character code">
              {boxes.map((c, i) => (
                <input
                  key={i}
                  ref={el => (inputs.current[i] = el)}
                  className={`code-box ${c ? 'filled' : ''} ${problem === 'bad' || problem === 'used' ? 'wrong' : ''}`}
                  value={c}
                  onChange={e => onChange(i, e)}
                  onKeyDown={e => onKeyDown(i, e)}
                  onPaste={e => onPaste(i, e)}
                  onFocus={e => e.currentTarget.select()}
                  aria-label={`Character ${i + 1}`}
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="go"
                  maxLength={LENGTH}
                  disabled={busy}
                />
              ))}
            </div>

            {hint && !problem && <p className="code-hint">Codes use letters and the numbers 2–9. There’s no 0, O, 1 or I.</p>}
            {problem && (
              <p className="code-error" role="alert">
                {PROBLEM_TEXT[problem]}
              </p>
            )}

            <button type="submit" className={`btn primary wide ${!complete || busy ? 'dim' : ''}`} disabled={!complete || busy}>
              {busy ? 'Connecting…' : 'Connect'}
            </button>
          </form>
        </div>

        <GetFluffer />

        <footer className="foot">
          Fluffer by Motorbunny · <a href="https://www.motorbunny.com" target="_blank" rel="noopener noreferrer">motorbunny.com</a>
        </footer>
      </main>
    </div>
  );
};

export default RemoteCode;
